import {useCallback, useEffect} from "react";
import {useRecoilState, useSetRecoilState} from "recoil";
import {
    ConstructorModel,
    OptionAttribute,
    ConstructorSize,
    ComplectationBlock,
    OptionsSelected,
} from "../data";
import {useGetConstructorBlocks} from "../data/queries";
import {
    stateApplicationHash,
    stateSelectedComplectations,
    stateSelectedAccessories,
    stateLoading,
    stateAccessoriesBlocks,
    stateComplectationBlocks,
    stateIsExternalLoad,
} from "../data/atoms";
import {
    generateHash,
    generateUid,
    getEmptySlotId,
    getUsedSlotId,
    isPinsBlockId,
    randomBetween,
    splitUid,
    toggleObjectProp,
} from "../utils/ApplicationUtils";
import {useNotification} from "./useNotification";
import {arraysIntersection} from "../utils/ArrayUtils";

export const useBuilderBlocks = (
    modelActive: ConstructorModel | null,
    sizeActive: ConstructorSize | null
) => {
    const {showError} = useNotification();

    const [isExternalLoad] = useRecoilState(stateIsExternalLoad);

    const [complectations, setComplectations] = useRecoilState(
        stateComplectationBlocks
    );
    const [accessories, setAccessories] = useRecoilState(stateAccessoriesBlocks);

    const setLoading = useSetRecoilState(stateLoading);
    const setComplectationSelected = useSetRecoilState(
        stateSelectedComplectations
    );
    const setAccessoireSelected = useSetRecoilState(stateSelectedAccessories);
    const setApplicationHash = useSetRecoilState(stateApplicationHash);

    const {data, isSuccess, isError, isFetching, isFetched} =
        useGetConstructorBlocks(modelActive?.id || null, sizeActive?.id || null);

    /**
     * Complectation option select
     *
     * will apply selected attribute color to SVG
     */
    const onComplectationSelect = useCallback(
        (uid: string, attribute: OptionAttribute | null = null) => {
            setComplectationSelected((prevState) =>
                selectedOptionUpdater(complectations, prevState, uid, attribute)
            );
        },
        [complectations, setComplectationSelected]
    );

    /**
     * Accessoire type select
     *
     * Will open block for accessoire variant select
     */
    const onAccessoireTypeSelect = useCallback(
        (uid: string) => {
            setAccessoireSelected((prevState) =>
                selectedBlockUpdated(accessories, prevState, uid)
            );
        },
        [accessories, setAccessoireSelected]
    );

    /**
     * Accessoire option select
     *
     * will apply selected attribute color to SVG
     */
    const onAccessoireSelect = useCallback(
        (uid: string, attribute: OptionAttribute | null = null) => {
            setAccessoireSelected((prevState) =>
                selectedOptionUpdater(accessories, prevState, uid, attribute)
            );
        },
        [accessories, setAccessoireSelected]
    );

    /**
     * Applies random-selected attributes
     */
    const onRandomGenerate = useCallback(() => {
        setComplectationSelected(() => getRandomComplectation(complectations));
    }, [complectations, setComplectationSelected]);

    /**
     * Reset selected data on success
     */
    useEffect(() => {
        if (!isExternalLoad && isFetched && isSuccess) {
            setComplectationSelected({});
            setAccessoireSelected({});

            setApplicationHash(generateHash());
        }
    }, [
        isSuccess,
        setComplectationSelected,
        setApplicationHash,
        setAccessoireSelected,
        isFetched,
        isExternalLoad,
    ]);

    /**
     * Check for errors
     * and data existed at least
     */
    useEffect(() => {
        if (isError || (isSuccess && !data?.complectations)) {
            showError(
                "Ошибка загрузки вариантов сумок",
                "Попробуйте обновить страницу или обратитесь к администратору магазина"
            );
        }
    }, [isError, isSuccess, data?.complectations, showError]);

    useEffect(() => {
        setLoading((prevState) => ({...prevState, data: isFetching}));
    }, [isFetching, setLoading]);

    useEffect(() => {
        setComplectations(data?.complectations || []);
        setAccessories(data?.accessories || []);
    }, [
        data?.accessories,
        data?.complectations,
        setAccessories,
        setComplectations,
    ]);

    return {
        onComplectationSelect,
        onAccessoireTypeSelect,
        onAccessoireSelect,
        onRandomGenerate,
    };
};

// -------------------------------------------
// Helper functions
// -------------------------------------------

const getRandomComplectation = (blocks: ComplectationBlock[] = []) => {
    const selected: OptionsSelected = {};

    blocks.forEach((block) => {
        const hasPins = isPinsBlockId(block.id);

        if (hasPins) {
            /**
             * To avoid duplicating of selected pins
             * Selected pin will be removed from the source array
             */
            const pinsItems = [...block.items];

            /**
             * Number of pins can be between
             * minimal required pins and number of slots
             */
            const min = block.slots?.requiredCount || 0;
            const max = block.slots?.positions.length || min;

            const pinsNumber = randomBetween(min, max);

            for (let pinIndex = 0; pinIndex <= pinsNumber - 1; pinIndex++) {
                const index = randomBetween(0, pinsItems.length - 1);
                const attributes = pinsItems[index].attributes;

                for (const attributesKey in attributes) {
                    const uid = generateUid(block.id, attributesKey, pinIndex.toString());

                    selected[uid] = {...attributes[attributesKey]};
                }

                /**
                 * Remove selected pin from source collection
                 * to avoid duplicating
                 */
                pinsItems.splice(index, 1);
            }
        } else {
            const index = randomBetween(0, block.items.length - 1);

            /**
             * Attributes without dependencies should be applied first
             */
            const attributes = Object.entries(block.items[index].attributes).sort(
                (a, b) => {
                    if ((a[1].dependencies === b[1].dependencies) === null) {
                        return 0;
                    }

                    if (a[1].dependencies === null) {
                        return -1;
                    }

                    if (b[1].dependencies === null) {
                        return 1;
                    }

                    return 0;
                }
            );

            attributes.forEach((el) => {
                const [attributesKey, attribute] = el;

                const uid = generateUid(block.id, attributesKey);

                let attributeValue = attribute;

                if (attribute.dependencies) {
                    const [firstDependencyUid] = Object.keys(attribute.dependencies);
                    const [dependencyKey] = splitUid(firstDependencyUid);

                    // find key of dependency that is already selected
                    const dependencyKeySelected = Object.keys(selected).find(
                        (key) => key === generateUid(dependencyKey, attributesKey)
                    );

                    // assign value of this key to dependent attribute
                    if (dependencyKeySelected) {
                        const value = selected[dependencyKeySelected];

                        if (value) {
                            attributeValue = value;
                        }
                    }
                }

                selected[uid] = attributeValue;
            });
        }
    });

    return selected;
};

/**
 * Used for selecting block without value
 *
 * For example accessoire type selecting (to show accessoire options section)
 */
const selectedBlockUpdated = (
    blocks: ComplectationBlock[],
    selected: OptionsSelected,
    uid: string
) => {
    const nextSelected = toggleObjectProp(selected, uid);

    if (nextSelected.hasOwnProperty(uid)) {
        /**
         * Checking excluded ids.
         *
         * If item has excluded ids, so they should be disabled (removed from selected)
         */
        const [blockUid] = splitUid(uid);
        const excludeIds =
            blocks.find((accessoire) => accessoire.id === blockUid)?.excludeIds || [];

        for (const selectedUid in nextSelected) {
            const [accessorieKey] = splitUid(selectedUid);

            if (excludeIds.includes(accessorieKey)) {
                delete nextSelected[selectedUid];
            }
        }
    }

    /**
     * Checking dependencies
     *
     * If it has some blocks which are dependent on a selected one,
     * and it was disabled, so we need to remove all dependent items
     */
    const selectedKeys = Object.keys(nextSelected);
    const selectedIds = selectedKeys.map(uid => splitUid(uid)[0]);

    for (const selectedUid in nextSelected) {
        const [accessorieKey] = splitUid(selectedUid);

        const accessoire = blocks.find((accessoire) => accessoire.id === accessorieKey);

        if (
            accessoire?.dependsOnIds != null &&
            arraysIntersection(accessoire?.dependsOnIds, selectedIds).length === 0
        ) {
            delete nextSelected[selectedUid];
        }
    }

    return nextSelected;
};

/**
 * Used for updating value after option select
 *
 * For example base color or accessoire variant
 */
const selectedOptionUpdater = (
    blocks: ComplectationBlock[],
    selected: OptionsSelected,
    uid: string,
    attribute: OptionAttribute | null
) => {
    const [blockUid] = splitUid(uid);
    const block = blocks.find((block) => block.id === blockUid);

    let selectedNext = {...selected};

    /**
     * No slots
     */
    if (!block?.slots?.positions.length) {
        selectedNext = {...selectedNext, [uid]: attribute};
    }

    /**
     * Slots existed
     */
    if (!!block?.slots?.positions.length && attribute) {
        const slotUid =
            getUsedSlotId(
                selectedNext,
                block.slots.positions.length,
                uid,
                attribute
            ) || getEmptySlotId(selectedNext, block.slots.positions.length, uid);

        if (slotUid) {
            selectedNext = toggleObjectProp(selectedNext, slotUid, attribute);
        }
    }

    /**
     * Selecting hidden attributes
     *
     * For example Maxi Mix-base bagstraps are hidden but should be colored
     */
    const hiddenAttributes =
        block?.attributes.filter((attribute) => attribute.isHidden) || [];

    if (hiddenAttributes.length && block && attribute) {
        hiddenAttributes.forEach((hidden) => {
            block.items.forEach((item) => {
                /**
                 * Находим зависимости скрытого атрибута в данном item
                 */
                const dependencies = item.attributes[hidden.id].dependencies;

                /**
                 * Если в данном item все зависимости скрытого атрибута выбраны (selected)
                 * то нужно применить цвет скрытого атрибута из данного item'а
                 */
                const hasSelectedValues =
                    dependencies &&
                    Object.keys(dependencies).every((uid) => {
                        const isSelected = selectedNext.hasOwnProperty(uid);
                        const id = selectedNext[uid]?.id;

                        return isSelected && id && dependencies[uid].includes(id);
                    });

                if (hasSelectedValues) {
                    const uid = generateUid(block.id, hidden.id);

                    selectedNext[uid] = item.attributes[hidden.id];
                }
            });
        });
    }

    return selectedNext;
};
